import { useCallback, useEffect, useState } from "react";

export const LOCALE_STORAGE_KEY = "obermind_i18nextLng";

export const SUPPORTED_LOCALES = [
  "en", "sv", "zh", "it", "es", "fr", "sk", "cs", "pl", "pt", "tr", "ja", "ar", "de", "da", "fi",
  "nb", "lt", "lv", "ru", "ur", "hi", "nl", "hu", "ro", "bg", "sr", "sq", "bn", "he", "vi", "fa",
] as const;

export type LocaleMessages = Record<string, string>;

export const getStoredLocale = (): string => {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY) || "en";
  return (SUPPORTED_LOCALES as readonly string[]).includes(stored) ? stored : "en";
};

const cache: Record<string, LocaleMessages | undefined> = {};

/**
 * English renders from each message's `defaultMessage`, so it needs no catalogue. Every other
 * locale is its own chunk, fetched on demand instead of shipping all of them in the entry bundle.
 */
export const loadMessages = async (locale: string): Promise<LocaleMessages | undefined> => {
  if (locale === "en" || !(SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
    return undefined;
  }
  if (locale in cache) {
    return cache[locale];
  }
  try {
    const module = await import(
      /* webpackChunkName: "locale-[request]" */
      `../translations/${locale}.json`
    );
    cache[locale] = module.default as LocaleMessages;
  } catch {
    cache[locale] = undefined;
  }
  return cache[locale];
};

/**
 * Current locale and its messages. Follows the `languageChange` document event that
 * `LanguageSelectModal` dispatches, and only switches once the new catalogue has loaded so
 * the page never flashes English in between.
 */
export const useLocaleMessages = (initial: { locale: string; messages: LocaleMessages | undefined }) => {
  const [state, setState] = useState(initial);

  useEffect(() => {
    document.documentElement.lang = state.locale;
    localStorage.setItem(LOCALE_STORAGE_KEY, state.locale);
  }, [state.locale]);

  const onLanguageChange = useCallback((ev: Event) => {
    const next = (ev as CustomEvent<{ value: string }>).detail?.value;
    if (!next) return;
    loadMessages(next).then((messages) => setState({ locale: next, messages }));
  }, []);

  useEffect(() => {
    document.addEventListener("languageChange", onLanguageChange);
    return () => document.removeEventListener("languageChange", onLanguageChange);
  }, [onLanguageChange]);

  return state;
};
